import { cssJoin } from '@volvo-cars/css/utils';
import { useLocale } from '@volvo-cars/react-locale-provider';
import type React from 'react';
import { forwardRef, useMemo } from 'react';
import { TextLikeInput } from './text-input';
import type { TextLikeProps } from './types';
import { UnitDisplay } from './unit-display';
import { useIds } from './use-ids';

export type CurrencyInputProps = Omit<
  TextLikeProps,
  | 'autoCapitalize'
  | 'autoComplete'
  | 'autoCorrect'
  | 'inputMode'
  | 'spellCheck'
  | 'variant'
> & {
  /**
   * The currency to display.
   */
  currency: string;

  /**
   * How to display the currency.
   * "symbol" uses a localized currency symbol such as € or kr if appropriate for the locale.
   * "code" always uses the ISO currency code.
   *
   * @default 'symbol'
   */
  currencyDisplay?: 'symbol' | 'code';

  /**
   * Aligns the input content.
   */
  align?: 'start' | 'end';

  /**
   * Specifies the visible width, in characters, of the input.
   */
  size?: number;

  /**
   * Value of the input.
   *
   * Makes the input controlled.
   */
  value?: number | string;

  /**
   * Default value of an uncontrolled input.
   */
  defaultValue?: number | string;

  /**
   * Fires immediately when the input’s value is changed by the user (for example, it fires on every keystroke).
   */
  onChange?: React.ChangeEventHandler<HTMLInputElement>;

  /**
   * Id of a `<datalist>` element with a list of predefined values to suggest to the user.
   */
  list?: string;

  /**
   * Hint the browser about the appropriate virtual keyboard for the type of expected data.
   *
   * Use `text` if you need support for negative numbers as the minus sign is not available on iPhone keyboards.
   *
   * @default 'numeric'
   */
  inputMode?: 'text' | 'numeric' | 'decimal';
};

/**
 * The `CurrencyInput` component displays the currency as a prefix or suffix
 * depending on the current `LocaleProvider` locale and currency.
 *
 * If you need to pass the currency as part of a form submitted as a `POST` request,
 * add a separate hidden input with the currency as the value.
 */
export const CurrencyInput = forwardRef<HTMLInputElement, CurrencyInputProps>(
  function CurrencyInput(
    {
      id,
      align,
      currency,
      currencyDisplay = 'symbol',
      inputMode = 'numeric',
      defaultValue,
      value,
      ...props
    },
    ref,
  ) {
    const locale = useLocale();
    const { rootId, suffixId, prefixId } = useIds(id);
    const formatter = useMemo(
      () =>
        new UnitDisplay({
          locale,
          currency,
          currencyDisplay,
        }),
      [locale, currency, currencyDisplay],
    );

    const contentBefore = formatter.prefix ? (
      <span className="text-secondary" id={prefixId}>
        {formatter.prefix}
      </span>
    ) : null;
    const contentAfter = formatter.suffix ? (
      <span className="text-secondary whitespace-nowrap" id={suffixId}>
        {formatter.suffix}
      </span>
    ) : null;

    return (
      <TextLikeInput
        {...props}
        ref={ref}
        type="text"
        variant="top-label"
        autoComplete="off"
        autoCapitalize="none"
        spellCheck={false}
        inputMode={inputMode}
        id={rootId}
        // Pass aria-labelledby to ensure screen readers read the currency symbols
        aria-labelledby={[
          formatter.prefix && prefixId,
          formatter.suffix && suffixId,
        ]
          .filter(Boolean)
          .join(' ')}
        contentBefore={contentBefore}
        contentAfter={contentAfter}
        inputClassName={cssJoin(align === 'end' && 'text-end')}
        value={value?.toString()}
        defaultValue={defaultValue?.toString()}
      />
    );
  },
);
